import { KnowledgeCheckReviewQuestion } from '../knowledge-check'
import { answerShare } from './review-answers'

export type QuestionSummary = {
	/** The base of every share on the question: everybody in the check. */
	participants: number
	/** How many answered this question. */
	answered?: { count: number }
	/** How many answered it right, and their share of the participants. */
	correct?: { count: number; percent: number }
}

const picks = (options: KnowledgeCheckReviewQuestion['options']): number =>
	options.reduce((sum, option) => sum + option.numberOfSelected, 0)

/**
 * What the review can truthfully say about one question as a whole.
 *
 * The review carries, per answer, how many picked it - not who. On a
 * single-select question everybody who answered picked exactly one answer, so
 * the picks add up to the people who answered, and the picks of the correct
 * answer to the people who got it right. On a multi-select question the picks
 * add up to more than the people, and nothing tells who picked what together:
 * neither figure can be had, so neither is given - never a guess.
 *
 * `showsCorrect` is whether the check reveals the correct answers at all; when
 * it does not, no figure may hint at them either.
 */
export const questionSummary = (
	question: KnowledgeCheckReviewQuestion,
	participantCount: number,
	showsCorrect: boolean
): QuestionSummary => {
	if (question.type !== 'SINGLE_SELECT') {
		return { participants: participantCount }
	}
	const correctOptions = question.options.filter(option => option.correct)
	const correctCount = picks(correctOptions)
	return {
		participants: participantCount,
		answered: { count: picks(question.options) },
		// A question with no answer marked right has no "right" to count
		correct:
			showsCorrect && correctOptions.length > 0
				? {
						count: correctCount,
						percent: answerShare(correctCount, participantCount),
				  }
				: undefined,
	}
}
